import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import HCaptcha from '@hcaptcha/react-hcaptcha'
import { type CaptchaPublicConfig } from '@/lib/captcha'
import { useCaptchaConfig } from '@/hooks/use-captcha-config'
import { cn } from '@/lib/utils'

export type CaptchaFieldHandle = {
  reset: () => void
  getToken: () => string | null
  execute: () => boolean
  isInvisible: () => boolean
  isReady: () => boolean
}

type CaptchaWidgetSize = 'compact' | 'normal' | 'invisible'

function resolveWidgetSize(config: CaptchaPublicConfig | null): CaptchaWidgetSize {
  const size = config?.widget?.size
  if (size === 'compact' || size === 'invisible') return size
  return 'normal'
}

type CaptchaFieldProps = {
  action: string
  variant?: 'default' | 'marketingTheme'
  onTokenChange?: (token: string | null) => void
  onMisconfigured?: () => void
  onExecuteError?: () => void
  onReady?: () => void
  disabled?: boolean
}

export const CaptchaField = forwardRef<CaptchaFieldHandle, CaptchaFieldProps>(
  function CaptchaField(
    { action, variant = 'default', onTokenChange, onMisconfigured, onExecuteError, onReady, disabled },
    ref,
  ) {
    const captchaRef = useRef<HCaptcha>(null)
    const pendingExecuteRef = useRef(false)
    const onMisconfiguredRef = useRef(onMisconfigured)
    onMisconfiguredRef.current = onMisconfigured
    const { config } = useCaptchaConfig()
    const [token, setToken] = useState<string | null>(null)
    const [widgetReady, setWidgetReady] = useState(false)

    useEffect(() => {
      if (config?.misconfigured) {
        onMisconfiguredRef.current?.()
      }
    }, [config?.misconfigured])

    const widgetSize = resolveWidgetSize(config)
    const isInvisible = widgetSize === 'invisible'

    const runExecute = () => {
      captchaRef.current?.execute()
    }

    useImperativeHandle(ref, () => ({
      reset: () => {
        captchaRef.current?.resetCaptcha()
        setToken(null)
        onTokenChange?.(null)
      },
      getToken: () => token,
      execute: () => {
        if (!widgetReady) {
          pendingExecuteRef.current = true
          return false
        }
        runExecute()
        return true
      },
      isInvisible: () => isInvisible,
      isReady: () => widgetReady,
    }))

    if (!config?.enabled || !config.siteKey) {
      return null
    }

    if (!config.actions?.[action]) {
      return null
    }

    const theme = config.widget?.theme === 'dark' ? 'dark' : 'light'

    return (
      <div
        className={cn(
          // Invisible: keep in DOM (not display:none / far off-screen) so execute() can open the challenge modal.
          isInvisible && 'absolute h-0 w-0 overflow-hidden opacity-0',
          !isInvisible &&
            variant === 'marketingTheme' &&
            'flex items-start justify-start',
          !isInvisible &&
            variant !== 'marketingTheme' &&
            'flex min-h-[78px] items-center justify-center rounded-md border border-border/60 bg-muted/20 px-3 py-2',
          !isInvisible && disabled && 'pointer-events-none opacity-60',
        )}
        aria-hidden={isInvisible}
      >
        <HCaptcha
          ref={captchaRef}
          sitekey={config.siteKey}
          size={widgetSize}
          theme={theme}
          onLoad={() => {
            setWidgetReady(true)
            onReady?.()
            if (pendingExecuteRef.current) {
              pendingExecuteRef.current = false
              runExecute()
            }
          }}
          onVerify={(value) => {
            setToken(value)
            onTokenChange?.(value)
          }}
          onExpire={() => {
            setToken(null)
            onTokenChange?.(null)
          }}
          onError={() => {
            setToken(null)
            onTokenChange?.(null)
            pendingExecuteRef.current = false
            onExecuteError?.()
          }}
        />
      </div>
    )
  },
)
